import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Stethoscope, RefreshCw } from "lucide-react";

/**
 * ClientDiagnosticDialog
 *
 * Modal that calls GET /api/admin/clients/{client_id}/messages/raw and
 * surfaces the raw fields of the last N messages, plus the counts the
 * Production Dashboard sees. Purpose: identify quickly why a video that
 * is visible in chat / Dropbox doesn't appear in the production task list
 * (wrong sender_role, wrong attachment.kind, premature task_edited, ...).
 *
 * Pure read-only: zero risk to run in production.
 */
export default function ClientDiagnosticDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/clients/${clientId}/messages/raw?limit=50`);
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (open) fetchData();
    else setData(null);
     
  }, [open, clientId, fetchData]);

  const sim = data?.task_filter_simulation || {};
  const messages = data?.recent_messages || [];

  // Roles allowed to upload attachments legitimately:
  // - client (uploads from Telegram or web portal)
  // - staff / agency (replies, lavorati uploaded from production dashboard)
  // - staff_direct (DirectUpload, ad-hoc lavorati uploaded for the client)
  // - system (auto-generated covers, system messages)
  const LEGIT_ATTACHMENT_ROLES = ["client", "staff", "staff_direct", "agency", "system"];

  // Anomaly heuristics — color-code the row red/yellow if something is
  // off, so the agent can scan a long list quickly.
  const isAnomalous = (m) => {
    // Only flag truly unexpected roles (e.g. "bot" or null with attachment)
    if (m.has_attachment && !LEGIT_ATTACHMENT_ROLES.includes(m.sender_role))
      return "wrong_role";
    if (m.has_attachment && m.att_too_large) return "too_large";
    if (m.has_attachment && !m.att_kind) return "missing_kind";
    if (
      m.has_attachment &&
      m.sender_role === "client" &&
      !["photo", "video", "document"].includes(m.att_kind)
    )
      return "non_task_kind";
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="client-diagnostic-dialog"
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Diagnostica messaggi — {clientName}
          </DialogTitle>
          <DialogDescription>
            Mostra i flag raw degli ultimi 50 messaggi del cliente. Utile per capire
            perché un video presente in chat/Dropbox non appare nella Coda Video.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-[var(--mp-muted)]">
            Caricamento…
          </div>
        )}

        {data && !loading && (
          <div className="space-y-4">
            {/* Counts summary */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Stat label="To-do" value={sim.todo} testid="diag-stat-todo" />
              <Stat label="Lavorati" value={sim.edited} testid="diag-stat-edited" />
              <Stat label="Pubblicati" value={sim.published} testid="diag-stat-published" />
              <Stat
                label="Tot task-kind"
                value={sim.base_total_task_kinds}
                hint="photo+video+document"
                testid="diag-stat-base"
              />
              <Stat
                label="Tutti gli att."
                value={sim.any_attachment_from_client}
                hint="qualunque kind"
                testid="diag-stat-any"
                tone={
                  sim.any_attachment_from_client > sim.base_total_task_kinds
                    ? "amber"
                    : "default"
                }
              />
            </div>

            {/* Auto-diagnosis hint */}
            <DiagnosisHint sim={sim} messages={messages} />

            {/* Refresh button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={fetchData}
                data-testid="diag-refresh-btn"
                className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--mp-ink)] text-white text-xs font-medium hover:bg-black"
              >
                <RefreshCw className="h-3 w-3" /> Aggiorna
              </button>
            </div>

            {/* Messages table */}
            <div className="rounded-2xl border border-[var(--mp-border)] overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[var(--mp-dash-bg)] text-[var(--mp-muted)] uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Mittente</th>
                    <th className="text-left p-2">Kind</th>
                    <th className="text-left p-2">Mime</th>
                    <th className="text-left p-2">Flag</th>
                    <th className="text-left p-2">File</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-[var(--mp-muted)]">
                        Nessun messaggio
                      </td>
                    </tr>
                  )}
                  {messages.map((m) => {
                    const anomaly = isAnomalous(m);
                    const rowCls = anomaly
                      ? anomaly === "wrong_role"
                        ? "bg-red-50"
                        : "bg-amber-50"
                      : "";
                    const senderCls =
                      m.sender_role === "client"
                        ? "text-emerald-700 font-semibold"
                        : ["staff", "staff_direct", "agency", "system"].includes(
                              m.sender_role,
                            )
                          ? "text-indigo-700 font-semibold"
                          : "text-red-700 font-semibold";
                    return (
                      <tr
                        key={m.id}
                        className={`border-t border-[var(--mp-border)] ${rowCls}`}
                        data-testid={`diag-row-${m.id}`}
                      >
                        <td className="p-2 font-mono whitespace-nowrap">
                          {new Date(m.created_at).toLocaleString("it-IT", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="p-2">
                          <span className={senderCls}>
                            {m.sender_role || "?"}
                          </span>
                          {m.sender_name ? (
                            <div className="text-[10px] text-[var(--mp-muted)]">
                              {m.sender_name}
                            </div>
                          ) : null}
                        </td>
                        <td className="p-2 font-mono">
                          {m.att_kind ? (
                            <span
                              className={
                                ["photo", "video", "document"].includes(m.att_kind)
                                  ? "text-emerald-700"
                                  : "text-amber-700"
                              }
                            >
                              {m.att_kind}
                            </span>
                          ) : m.has_attachment ? (
                            <span className="text-red-700">null</span>
                          ) : (
                            <span className="text-[var(--mp-muted)]">—</span>
                          )}
                        </td>
                        <td className="p-2 font-mono text-[10px]">{m.att_mime || "—"}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {m.task_edited && (
                              <Pill color="amber">edited</Pill>
                            )}
                            {m.task_published && (
                              <Pill color="indigo">published</Pill>
                            )}
                            {m.att_too_large && <Pill color="red">large</Pill>}
                            {m.group_id && <Pill color="gray">group</Pill>}
                          </div>
                        </td>
                        <td className="p-2 font-mono text-[10px] max-w-[180px] truncate">
                          {m.att_file_name || m.text_preview || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Help footer */}
            <div className="rounded-2xl bg-[var(--mp-dash-bg)] p-3 text-[11px] text-[var(--mp-muted)] leading-relaxed">
              <strong>Legenda mittente</strong>: <span className="text-emerald-700 font-semibold">client</span> = invio dal cliente · <span className="text-indigo-700 font-semibold">staff/staff_direct/system</span> = upload dall&apos;agenzia (lavorati/copertine, legittimi) · <span className="text-red-700 font-semibold">rosso</span> = ruolo inatteso.
              <br />
              <strong>Righe colorate</strong>: rossa = ruolo inatteso · gialla = problema (file troppo grande / kind null / kind non utilizzabile lato cliente).
              <br />
              <strong>Pillole</strong>: <Pill color="amber">edited</Pill> = task lavorato · <Pill color="indigo">published</Pill> = task pubblicato · <Pill color="red">large</Pill> = file &gt; 20 MB (Telegram non lo scarica) · <Pill color="gray">group</Pill> = parte di un gruppo.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, hint, tone = "default", testid }) {
  const toneCls =
    tone === "amber"
      ? "bg-amber-50 border-amber-300"
      : "bg-white border-[var(--mp-border)]";
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-2 text-center ${toneCls}`}
    >
      <div className="text-2xl font-display tracking-tighter leading-none tabular-nums">
        {value ?? "—"}
      </div>
      <div className="text-[9px] uppercase tracking-[0.15em] text-[var(--mp-muted)] mt-1">
        {label}
      </div>
      {hint && (
        <div className="text-[9px] text-[var(--mp-muted)] italic">{hint}</div>
      )}
    </div>
  );
}

function Pill({ children, color }) {
  const map = {
    amber: "bg-amber-100 text-amber-800",
    indigo: "bg-indigo-100 text-indigo-800",
    red: "bg-red-100 text-red-800",
    gray: "bg-gray-200 text-gray-700",
  };
  return (
    <span className={`inline-flex items-center px-1.5 rounded text-[9px] font-semibold ${map[color] || map.gray}`}>
      {children}
    </span>
  );
}

function DiagnosisHint({ sim, messages }) {
  const LEGIT = ["client", "staff", "staff_direct", "agency", "system"];
  const lines = [];

  // 1) Real bug: video > 20 MB skipped by Telegram
  const tooLarge = messages.filter(
    (m) => m.has_attachment && m.att_too_large,
  );
  if (tooLarge.length > 0) {
    lines.push(
      `🚨 ${tooLarge.length} file > 20 MB scartati da Telegram (flag "large"). Il cliente deve comprimere il video o usare il portale per caricare.`,
    );
  }

  // 2) Client attachments that aren't valid task-kinds (voice/audio/sticker)
  const nonTask =
    (sim.any_attachment_from_client || 0) - (sim.base_total_task_kinds || 0);
  if (nonTask > 0) {
    lines.push(
      `ℹ️ ${nonTask} allegati del cliente non sono task (probabilmente note vocali, audio o sticker). Normale, non sono bug.`,
    );
  }

  // 3) Hard bug: client sent stuff but nothing became a task
  if (sim.any_attachment_from_client > 0 && sim.base_total_task_kinds === 0) {
    lines.push(
      "⚠️ Il cliente ha mandato allegati ma NESSUNO è classificato come photo/video/document. Probabilmente tutte note vocali/audio — oppure bug di classificazione.",
    );
  }

  // 4) Truly unexpected sender_role (excludes legitimate staff/system uploads)
  const wrongRole = messages.filter(
    (m) => m.has_attachment && !LEGIT.includes(m.sender_role),
  ).length;
  if (wrongRole > 0) {
    lines.push(
      `🚨 ${wrongRole} messaggio/i con attachment hanno sender_role inatteso (es. bot/null). Possibile bug del webhook.`,
    );
  }

  // 5) Kind null bug
  const nullKind = messages.filter(
    (m) => m.has_attachment && !m.att_kind,
  ).length;
  if (nullKind > 0) {
    lines.push(
      `⚠️ ${nullKind} messaggio/i con attachment ma attachment.kind = null. Bug di salvataggio.`,
    );
  }

  // 6) Task-kinds exist but no tab shows them
  if (
    sim.todo === 0 &&
    sim.edited === 0 &&
    sim.published === 0 &&
    sim.base_total_task_kinds > 0
  ) {
    lines.push(
      "⚠️ Ci sono task-kind nel DB ma 0 in tutti i tab. Verifica i filtri di sender_role.",
    );
  }

  // 7) Counts breakdown summary (always shown for transparency)
  const summary =
    sim.base_total_task_kinds > 0
      ? `📊 Task: ${sim.todo || 0} to-do + ${sim.edited || 0} lavorati + ${sim.published || 0} pubblicati = ${(sim.todo || 0) + (sim.edited || 0) + (sim.published || 0)} totali (su ${sim.base_total_task_kinds} task-kind nel DB).`
      : null;

  // Classify severity: any "🚨" line = critical
  const hasCritical = lines.some((l) => l.startsWith("🚨"));
  const allInfo = lines.length > 0 && lines.every((l) => l.startsWith("ℹ️"));

  if (lines.length === 0) {
    return (
      <div
        className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900"
        data-testid="diag-hints"
      >
        ✓ Nessuna anomalia rilevata dai controlli automatici.
        {summary && (
          <div className="mt-1 text-emerald-800">{summary}</div>
        )}
      </div>
    );
  }

  const toneCls = hasCritical
    ? "bg-red-50 border-red-300 text-red-900"
    : allInfo
      ? "bg-sky-50 border-sky-300 text-sky-900"
      : "bg-amber-50 border-amber-300 text-amber-900";

  return (
    <div
      className={`rounded-2xl border p-3 text-xs space-y-1 ${toneCls}`}
      data-testid="diag-hints"
    >
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
      {summary && <div className="pt-1 opacity-80">{summary}</div>}
    </div>
  );
}
